import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageSquare, Search, Bookmark, X } from 'lucide-react';
import AppShell from '@/components/AppShell';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CompactPromptBox } from '@/components/CompactPromptBox';
import { ConversationListItem } from '@/components/ConversationListItem';
import { Skeleton } from '@/components/Skeleton';
import { useChat } from '@/context/ChatContext';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// Group conversations by date category
function groupConversationsByDate(conversations) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const last7 = new Date(today);
  last7.setDate(last7.getDate() - 7);
  const last30 = new Date(today);
  last30.setDate(last30.getDate() - 30);

  const groups = {
    Today: [],
    Yesterday: [],
    'Last 7 Days': [],
    'Last 30 Days': [],
    Older: [],
  };

  conversations.forEach((conv) => {
    const convDate = new Date(conv.updated_at);
    const convDay = new Date(
      convDate.getFullYear(),
      convDate.getMonth(),
      convDate.getDate()
    );

    if (convDay.getTime() >= today.getTime()) {
      groups.Today.push(conv);
    } else if (convDay.getTime() >= yesterday.getTime()) {
      groups.Yesterday.push(conv);
    } else if (convDay.getTime() >= last7.getTime()) {
      groups['Last 7 Days'].push(conv);
    } else if (convDay.getTime() >= last30.getTime()) {
      groups['Last 30 Days'].push(conv);
    } else {
      groups.Older.push(conv);
    }
  });

  return groups;
}

export default function ConversationsPage() {
  const chat = useChat();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  const [total, setTotal] = useState(0);
  const debounceRef = useRef(null);

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: 1, limit: 100 };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (showSavedOnly) params.is_saved = true;

      const response = await api.get('/conversations', { params });
      setConversations(response.data.conversations || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, showSavedOnly]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const handleStartConversation = (message) => {
    if (!message.trim()) return;
    chat.startNewChat(message);
  };

  const handleOpenConversation = (conversationId) => {
    chat.openConversation(conversationId);
  };

  const handleToggleSaved = async (conversationId, newSavedState) => {
    // Optimistic update
    setConversations((prev) =>
      prev.map((c) =>
        c.id === conversationId ? { ...c, is_saved: newSavedState } : c
      )
    );
    try {
      await api.put(`/conversations/${conversationId}`, { is_saved: newSavedState });
    } catch (error) {
      // Revert on failure
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId ? { ...c, is_saved: !newSavedState } : c
        )
      );
      toast.error('Failed to update bookmark');
    }
  };

  const handleDelete = async (conversationId) => {
    // Optimistic removal
    const removed = conversations.find((c) => c.id === conversationId);
    setConversations((prev) => prev.filter((c) => c.id !== conversationId));
    setTotal((prev) => prev - 1);

    // Close panel if this conversation is open
    if (chat.activeConversation?.id === conversationId) {
      chat.closePanel();
    }

    try {
      await api.delete(`/conversations/${conversationId}`);
      toast.success('Conversation deleted');
    } catch (error) {
      // Revert on failure
      if (removed) {
        setConversations((prev) => [...prev, removed]);
        setTotal((prev) => prev + 1);
      }
      toast.error('Failed to delete conversation');
    }
  };

  const groupedConversations = groupConversationsByDate(conversations);
  const hasConversations = conversations.length > 0;

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold">Anchor Sessions</h1>
          <p className="text-muted-foreground">
            Start a new conversation or continue where you left off
          </p>
        </div>

        {/* New Conversation Input */}
        <Card className="glass-elevated p-6">
          <CompactPromptBox
            onSubmit={handleStartConversation}
            placeholder="Ask Anchor anything about your projects..."
          />
        </Card>

        {/* Search and Filters */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 glass-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
              </button>
            )}
          </div>
          <Button
            variant={showSavedOnly ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowSavedOnly(!showSavedOnly)}
            className={cn(
              'gap-1.5',
              showSavedOnly && 'bg-primary/15 text-primary hover:bg-primary/20'
            )}
          >
            <Bookmark className={cn('h-4 w-4', showSavedOnly && 'fill-current')} />
            Saved
          </Button>
        </div>

        {/* Conversations List */}
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : hasConversations ? (
          <div className="space-y-6">
            {Object.entries(groupedConversations).map(
              ([group, groupConvs]) =>
                groupConvs.length > 0 && (
                  <div key={group} className="space-y-3">
                    <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                      {group}
                    </h2>
                    <div className="space-y-2">
                      {groupConvs.map((conversation) => (
                        <ConversationListItem
                          key={conversation.id}
                          conversation={{
                            id: conversation.id,
                            projectName: conversation.project_name || 'General',
                            lastMessage: conversation.last_message_snippet || conversation.title,
                            timestamp: conversation.updated_at,
                            is_saved: conversation.is_saved,
                          }}
                          onClick={() => handleOpenConversation(conversation.id)}
                          isActive={chat.activeConversation?.id === conversation.id}
                          onToggleSaved={handleToggleSaved}
                          onDelete={handleDelete}
                        />
                      ))}
                    </div>
                  </div>
                )
            )}
          </div>
        ) : (
          // Empty State
          <Card className="glass-subtle p-12">
            <div className="flex flex-col items-center justify-center text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <MessageSquare className="w-8 h-8 text-primary" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-semibold">
                  {searchQuery || showSavedOnly ? 'No matching conversations' : 'No conversations yet'}
                </h3>
                <p className="text-muted-foreground max-w-md">
                  {searchQuery || showSavedOnly
                    ? 'Try adjusting your search or filters.'
                    : 'Start a conversation with Anchor to get insights about your projects, understand blockers, and track progress.'}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Total count */}
        {!loading && total > 0 && (
          <p className="text-xs text-muted-foreground text-center">
            {total} conversation{total !== 1 ? 's' : ''}
          </p>
        )}
      </div>
    </AppShell>
  );
}
